import React, { useEffect, useState } from "react";
import { Card, Grid, Tab, Tabs, Chip, Button } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { projectActions } from "store/project-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import { startOfLocalDay } from "utils/dateUtil";
import {
  fetchAllProjects,
  fetchAllMilestones,
  fetchAllResources,
  deleteMilestone,
} from "app/services/project.service";
import { ProjectDataGrid } from "./ProjectDataGrid";
import { MilestoneDataGrid } from "./MilestoneDataGrid";
import { ResourceDataGrid } from "./ResourceDataGrid";
import AddMilestoneDialog from "./AddMilestoneDialog";
import { ConfirmDialog } from "../ConfirmDialog";

export const ProjectManagerView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editMilestone, setEditMilestone] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const projects = useSelector((state) => state.project.projects);
  const milestones = useSelector((state) => state.project.milestones);
  const resources = useSelector((state) => state.project.resources);

  const loadData = () => {
    setIsLoading(true);
    Promise.all([
      fetchAllProjects().then((data) => dispatch(projectActions.setProjects(data))),
      fetchAllMilestones().then((data) => dispatch(projectActions.setMilestones(data))),
      fetchAllResources().then((data) => dispatch(projectActions.setResources(data))),
    ])
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteMilestone = (item) => {
    setDeleteItem(item);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteItem) {
      await deleteMilestone(deleteItem.id);
      fetchAllMilestones().then((data) => dispatch(projectActions.setMilestones(data)));
    }
    setDeleteItem(null);
  };

  const activeProjects = projects.filter((p) => p.status !== "COMPLETED" && p.status !== "ON_HOLD");
  // Calendar-day comparisons, matching MilestoneDataGrid.isOverdue. Comparing a date-only string
  // against new Date() asked the instant question instead, so from 05:30 IST a milestone due today
  // counted as overdue in this stat card while its own row in the grid below rendered as not
  // overdue — the same screen giving two answers.
  const upcomingMilestones = milestones.filter(
    (m) => m.status !== "COMPLETED" && startOfLocalDay(m.dueDate) >= startOfLocalDay(new Date())
  );
  const overdueMilestones = milestones.filter(
    (m) => m.status !== "COMPLETED" && startOfLocalDay(m.dueDate) < startOfLocalDay(new Date())
  );

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="info">Active Projects</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{activeProjects.length}</MDTypography>
              <MDTypography variant="caption">In Progress</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="success">Upcoming</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{upcomingMilestones.length}</MDTypography>
              <MDTypography variant="caption">Milestones</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="error">Overdue</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{overdueMilestones.length}</MDTypography>
              <MDTypography variant="caption">Milestones</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="warning">Team Size</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{resources.length}</MDTypography>
              <MDTypography variant="caption">Allocated Resources</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Main Content with Tabs */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  My Projects
                  {activeProjects.length > 0 && <Chip size="small" label={activeProjects.length} color="primary" />}
                </MDBox>} />
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  Milestones
                  {overdueMilestones.length > 0 && <Chip size="small" label={overdueMilestones.length} color="error" />}
                </MDBox>} />
                <Tab label="Team Resources" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <ProjectDataGrid projects={activeProjects} isLoading={isLoading} showActions={false} />
            )}
            {tabValue === 1 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditMilestone(null); setMilestoneDialogOpen(true); }} variant="contained" color="success">
                    Add Milestone
                  </Button>
                </MDBox>
                <MilestoneDataGrid milestones={milestones} isLoading={isLoading} onEdit={(m) => { setEditMilestone(m); setMilestoneDialogOpen(true); }} onDelete={handleDeleteMilestone} />
              </MDBox>
            )}
            {tabValue === 2 && (
              <ResourceDataGrid resources={resources} isLoading={isLoading} showActions={false} />
            )}
          </Card>
        </Grid>
      </Grid>

      <AddMilestoneDialog open={milestoneDialogOpen} setOpen={setMilestoneDialogOpen} onSuccess={() => fetchAllMilestones().then((data) => dispatch(projectActions.setMilestones(data)))} editObject={editMilestone} projects={projects} />
      <ConfirmDialog title="Delete Milestone?" open={confirmOpen} setOpen={setConfirmOpen} onConfirm={confirmDelete} yesButtonLabel="Delete" noButtonLabel="Cancel">
        Are you sure you want to delete this milestone?
      </ConfirmDialog>
    </MDBox>
  );
};

